import { useCallback, useEffect, useMemo, useState } from 'react';
import { useClientStore } from '@core/client/useClientStore';
import type { Client, Site } from '@core/generated/gql-types';
import { useSiteStore } from '@core/site/useSiteStore';
import { useTenantId } from '@/contexts/TenantContext';
import type { ClientsListView } from '@/modules/clients/views/clientsList.view';

const EMPTY_SITE_NAMES_BY_CLIENT_ID = new Map<string, string[]>();

const buildSiteNamesByClientId = (sites: Site[]): Map<string, string[]> => {
  const namesByClientId = new Map<string, Set<string>>();

  for (const site of sites) {
    const name = site.name?.trim();
    if (!site.clientId || !name) continue;
    const names = namesByClientId.get(site.clientId) ?? new Set<string>();
    names.add(name);
    namesByClientId.set(site.clientId, names);
  }

  return new Map(
    [...namesByClientId.entries()].map(([clientId, names]) => [
      clientId,
      [...names],
    ]),
  );
};

type UseClientsListData = {
  clients: Client[];
  isLoading: boolean;
  hasFetchedClients: boolean;
  tenantId: string | null;
  siteNamesByClientId: ReadonlyMap<string, readonly string[]>;
};

export const useClientsListData = (
  view: ClientsListView,
): UseClientsListData => {
  const tenantId = useTenantId();
  const { clients, fetchClientsForTenant } = useClientStore();
  const fetchSitesForClients = useSiteStore(
    (state) => state.fetchSitesForClients,
  );
  const [hasFetchedClients, setHasFetchedClients] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [siteNamesByClientId, setSiteNamesByClientId] = useState<
    ReadonlyMap<string, readonly string[]>
  >(EMPTY_SITE_NAMES_BY_CLIENT_ID);

  const fetchClients = useCallback(async () => {
    if (!tenantId) return;
    if (view.query.clients.strategy !== 'tenant') return;

    setIsLoading(true);
    try {
      await fetchClientsForTenant(tenantId, undefined, undefined, true);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, fetchClientsForTenant, view.query.clients.strategy]);

  useEffect(() => {
    if (hasFetchedClients || !tenantId) return;
    if (view.query.clients.strategy !== 'tenant') return;

    fetchClients()
      .then(() => setHasFetchedClients(true))
      .catch((error) => {
        console.error('Error fetching clients:', error);
      });
  }, [tenantId, hasFetchedClients, fetchClients, view.query.clients.strategy]);

  const clientIds = useMemo(
    () =>
      (clients ?? [])
        .map((client) => client.id)
        .filter((clientId) => clientId.length > 0),
    [clients],
  );

  useEffect(() => {
    if (!hasFetchedClients) return;
    if (view.query.clients.strategy !== 'tenant') return;

    if (clientIds.length === 0) {
      setSiteNamesByClientId(EMPTY_SITE_NAMES_BY_CLIENT_ID);
      return;
    }

    let cancelled = false;
    fetchSitesForClients(clientIds)
      .then((sites) => {
        if (cancelled) return;
        setSiteNamesByClientId(buildSiteNamesByClientId(sites));
      })
      .catch((error) => {
        console.error('Error fetching sites for clients:', error);
      });

    // Cleanup function to cancel the fetch if the component unmounts.
    // This is a workaround to avoid fetching sites for clients that are no longer in the list.
    // Only the most recent fetch should be allowed to complete.
    return () => {
      cancelled = true;
    };
  }, [
    hasFetchedClients,
    clientIds,
    fetchSitesForClients,
    view.query.clients.strategy,
  ]);

  return {
    clients: clients ?? [],
    isLoading,
    hasFetchedClients,
    tenantId,
    siteNamesByClientId,
  };
};
